import { getFromStorage, saveToStorage } from '../storage';
import { INPUT_CONTROL_TYPE_ENUM } from '../../config/constants';

const formatInputAnswers = (answers) =>
  answers
    ?.filter(
      (a) =>
        (a.value || a.file) &&
        (a.fkRegFormControlType !== INPUT_CONTROL_TYPE_ENUM.CHECKBOX ||
          a.value?.toLowerCase() === 'yes')
    )
    .map(
      ({
        fkEcomVendorProductInput,
        previewImage,
        value,
        fkRegFormControlType
      }) => ({
        fkEcomVendorProductInput,
        previewImage,
        value:
          fkRegFormControlType === INPUT_CONTROL_TYPE_ENUM.CHECKBOX
            ? 'yes'
            : value
      })
    ) || [];

const matchInputAnswers = (pAnswers, cAnswers) => {
  const pAnswersFormatted = formatInputAnswers(pAnswers);
  const cAnswersFormatted = formatInputAnswers(cAnswers);

  return (
    cAnswersFormatted.length === pAnswersFormatted.length &&
    pAnswersFormatted.every((a) =>
      cAnswersFormatted.some(
        (e) =>
          e.fkEcomVendorProductInput === a.fkEcomVendorProductInput &&
          (!e.value && !!e.previewImage
            ? a.previewImage === e.previewImage
            : e.value?.trim() === a.value?.trim())
      )
    )
  );
};

const matchDesignAnswers = (pAnswers, cAnswers) =>
  pAnswers?.length === cAnswers?.length &&
  pAnswers.every((a) => {
    const match = cAnswers.find(
      (e) =>
        e.fkEcomImagePosition === a.fkEcomImagePosition &&
        e.fkEcomVendorProductPrintDesign === a.fkEcomVendorProductPrintDesign &&
        e.ecomOrderProductPrintImageAnswers.every((oa) =>
          a.ecomOrderProductPrintImageAnswers.find(
            (ca) =>
              ca.fkEcomVendorProductPrintDesignItem ===
                oa.fkEcomVendorProductPrintDesignItem &&
              ca.value?.trim() === oa.value?.trim()
          )
        )
    );
    return !!match;
  });

const useCartStorageController = ({
  isAdminCheckout = false,
  pkEcomOrder = null
}) => {
  const defaultCartState = {
    secureId: null,
    email: null,
    ecomStoreCartProducts: [],
    ecomStoreCartPackages: [],
    shippingOptions: [],
    shippingAddress: null,
    pkEcomOrder
  };

  const getCartStorageId = (pkEcomStore) =>
    isAdminCheckout
      ? `store-cart-${pkEcomStore}-admin`
      : `store-cart-${pkEcomStore}`;

  const resetCart = (pkEcomStore) => {
    const cart = {
      ...defaultCartState,
      fkEcomStore: pkEcomStore
    };
    saveToStorage(
      getCartStorageId(pkEcomStore, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const getGuestCartStorage = (pkEcomStore) => {
    const idString = getCartStorageId(pkEcomStore, isAdminCheckout);
    const cartString = getFromStorage(idString);
    if (cartString) {
      try {
        const cart = JSON.parse(cartString);
        cart.ecomStoreCartProducts = cart.ecomStoreCartProducts || [];
        cart.ecomStoreCartPackages = cart.ecomStoreCartPackages || [];
        cart.shippingOptions = cart.shippingOptions || [];

        return cart;
      } catch (e) {
        console.log('Error parsing cart from storage, resetting cart', e);
      }
    }

    return resetCart(pkEcomStore);
  };

  const setGuestCartProductsAndPackages = (
    pkEcomStore,
    { ecomStoreCartProducts, ecomStoreCartPackages }
  ) => {
    const cart = getGuestCartStorage(pkEcomStore, isAdminCheckout);
    cart.ecomStoreCartProducts = ecomStoreCartProducts;
    cart.ecomStoreCartPackages = ecomStoreCartPackages;

    saveToStorage(
      getCartStorageId(pkEcomStore, isAdminCheckout),
      JSON.stringify(cart)
    );

    return cart;
  };

  const setCartEmail = (pkEcomStore, email) => {
    const cart = getGuestCartStorage(pkEcomStore, isAdminCheckout);
    cart.email = email;
    saveToStorage(
      getCartStorageId(pkEcomStore, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const setCartFundraiser = (
    pkEcomStore,
    { fkEcomStoreRosterPlayer, customInput }
  ) => {
    const cart = getGuestCartStorage(pkEcomStore, isAdminCheckout);
    cart.fundraiser = { fkEcomStoreRosterPlayer, customInput };
    saveToStorage(
      getCartStorageId(pkEcomStore, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const setCartSecureId = (pkEcomStore, secureId) => {
    const cart = getGuestCartStorage(pkEcomStore, isAdminCheckout);
    cart.secureId = secureId;
    saveToStorage(
      getCartStorageId(pkEcomStore, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const setCartShippingAddress = (pkEcomStore, ecomCustomerAddress) => {
    const cart = getGuestCartStorage(pkEcomStore, isAdminCheckout);
    cart.shippingAddress = ecomCustomerAddress;
    saveToStorage(
      getCartStorageId(pkEcomStore, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const setCartShippingOptions = (pkEcomStore, shippingOptions) => {
    const cart = getGuestCartStorage(pkEcomStore, isAdminCheckout);
    cart.shippingOptions = shippingOptions;
    saveToStorage(
      getCartStorageId(pkEcomStore, isAdminCheckout),
      JSON.stringify(cart)
    );
    return cart;
  };

  const updateGuestCartProduct = (
    cartProduct,
    productRequests = [],
    appendQuantity = false
  ) => {
    const productRequest = productRequests.find(
      (c) =>
        c.pkEcomStoreCartProduct === cartProduct.pkEcomStoreCartProduct ||
        (c.uuid === cartProduct.uuid &&
          matchInputAnswers(
            cartProduct.ecomStoreCartProductInputAnswers,
            c.ecomStoreCartProductInputAnswers
          ) &&
          matchDesignAnswers(
            cartProduct.ecomOrderProductPrintImages || [],
            c.ecomOrderProductPrintImages || []
          ))
    );

    if (!productRequest) {
      return cartProduct;
    }

    return {
      ...cartProduct,
      count: appendQuantity
        ? cartProduct.count + productRequest.count
        : productRequest.count,
      ecomStoreCartProductInputAnswers: formatInputAnswers(
        productRequest.ecomStoreCartProductInputAnswers || []
      ),
      ecomOrderProductPrintImages:
        productRequest.ecomOrderProductPrintImages || []
    };
  };

  const addOrUpdateGuestCartProducts = (
    guestCartProducts,
    productRequests = []
  ) => {
    const updatedCartProducts = guestCartProducts.map((cartProduct) =>
      updateGuestCartProduct(cartProduct, productRequests, true)
    );

    const newCartProducts =
      productRequests
        ?.filter(
          (c) =>
            !guestCartProducts.some(
              (p) =>
                c.uuid === p.uuid &&
                matchInputAnswers(
                  p.ecomStoreCartProductInputAnswers,
                  c.ecomStoreCartProductInputAnswers
                ) &&
                matchDesignAnswers(
                  p.ecomOrderProductPrintImages,
                  c.ecomOrderProductPrintImages
                )
            )
        )
        .map((c, index) => ({
          pkEcomStoreCartProduct: Date.now() + index,
          uuid: c.uuid,
          count: c.count,
          ecomStoreCartProductInputAnswers:
            formatInputAnswers(c.ecomStoreCartProductInputAnswers) || [],
          ecomOrderProductPrintImages: c.ecomOrderProductPrintImages || [],
          addToGuestCart: true
        })) ?? [];

    return [...updatedCartProducts, ...newCartProducts];
  };

  const updateGuestCartPackage = (cartProduct, packageRequests = []) => {
    const packageRequest = packageRequests.find(
      (c) => c.pkEcomStoreCartPackage === cartProduct.pkEcomStoreCartPackage
    );

    if (!packageRequest) {
      return cartProduct;
    }

    const products = addOrUpdateGuestCartProducts(
      [],
      packageRequest.ecomStoreCartProducts
    );

    return {
      ...cartProduct,
      count: packageRequest.count,
      ecomStoreCartProducts:
        products.length > 0 ? products : cartProduct.ecomStoreCartProducts
    };
  };

  const addOrUpdateGuestCartPackages = (
    guestCartPackages,
    packageRequests = []
  ) => {
    const updatedCartPackages = guestCartPackages.map((cartPackage) =>
      updateGuestCartPackage(cartPackage, packageRequests)
    );

    const newCartPackages =
      packageRequests
        ?.filter((c) => !c.pkEcomStoreCartPackage)
        .map((c, index) => ({
          ...c,
          pkEcomStoreCartPackage: Date.now() + index,
          ecomStoreCartProducts: addOrUpdateGuestCartProducts(
            [],
            c.ecomStoreCartProducts
          ),
          addToGuestCart: true
        })) ?? [];

    return [...updatedCartPackages, ...newCartPackages];
  };

  const addGuestCartProductsAndPackages = (
    guestCart,
    { ecomStoreCartProducts, ecomStoreCartPackages }
  ) => {
    const updatedCartProducts = addOrUpdateGuestCartProducts(
      guestCart.ecomStoreCartProducts,
      ecomStoreCartProducts
    );

    const updatedCartPackages = addOrUpdateGuestCartPackages(
      guestCart.ecomStoreCartPackages,
      ecomStoreCartPackages
    );

    return {
      ecomStoreCartProducts: updatedCartProducts,
      ecomStoreCartPackages: updatedCartPackages
    };
  };

  const updateGuestCartProductsAndPackages = (
    guestCart,
    { ecomStoreCartProducts, ecomStoreCartPackages }
  ) => {
    const updatedCartProducts = guestCart.ecomStoreCartProducts.map(
      (cartProduct) =>
        updateGuestCartProduct(cartProduct, ecomStoreCartProducts)
    );
    const updatedCartPackages = guestCart.ecomStoreCartPackages.map(
      (cartPackage) =>
        updateGuestCartPackage(cartPackage, ecomStoreCartPackages)
    );

    return {
      ecomStoreCartProducts: updatedCartProducts,
      ecomStoreCartPackages: updatedCartPackages
    };
  };

  const onFetchGuestCartCallback = (
    pkEcomStore,
    data,
    { ecomStoreCartProducts, ecomStoreCartPackages }
  ) => {
    const filteredProducts = ecomStoreCartProducts
      .filter((requestProduct) =>
        data.ecomStoreCartProducts.some(
          (cartProduct) =>
            requestProduct.pkEcomStoreCartProduct ===
            cartProduct.pkEcomStoreCartProduct
        )
      )
      .map((p) => {
        const cartProduct = data.ecomStoreCartProducts.find(
          (c) => c.pkEcomStoreCartProduct === p.pkEcomStoreCartProduct
        );
        return {
          ...p,
          addToGuestCart: false,
          count: cartProduct.count,
          ecomOrderProductPrintImages: p.ecomOrderProductPrintImages.map(
            (pi) => ({
              ...pi,
              src:
                cartProduct.ecomOrderProductPrintImages?.find(
                  (cpi) => cpi.fkEcomImagePosition === pi.fkEcomImagePosition
                )?.src || pi.src
            })
          )
        };
      });

    const filteredPackages = ecomStoreCartPackages
      .filter((requestPackage) =>
        data.ecomStoreCartPackages.some(
          (cartPackage) =>
            requestPackage.pkEcomStoreCartPackage ===
            cartPackage.pkEcomStoreCartPackage
        )
      )
      .map((p) => {
        const cartPackage = data.ecomStoreCartPackages.find(
          (c) => c.pkEcomStoreCartPackage === p.pkEcomStoreCartPackage
        );
        return {
          ...p,
          addToGuestCart: false,
          count: cartPackage.count
        };
      });

    setGuestCartProductsAndPackages(pkEcomStore, {
      ecomStoreCartProducts: filteredProducts,
      ecomStoreCartPackages: filteredPackages
    });
  };

  return {
    getGuestCartStorage,
    setCartEmail,
    setCartSecureId,
    setCartShippingAddress,
    setCartShippingOptions,
    setCartFundraiser,
    resetCart,
    formatInputAnswers,
    matchInputAnswers,
    matchDesignAnswers,
    addGuestCartProductsAndPackages,
    updateGuestCartProductsAndPackages,
    onFetchGuestCartCallback
  };
};

export {
  useCartStorageController,
  formatInputAnswers,
  matchInputAnswers,
  matchDesignAnswers
};
